import { memo, useEffect, useMemo, useState } from 'react';
import './MonitorCell.scss';

import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useRecoilValue } from 'recoil';

import {
  Button,
  ComposedChart,
  Icons,
  PendingContent,
  Spinner,
  Status,
  Tooltip,
} from 'plume-ui';
import { ComposedChartDataItem } from 'plume-ui/dist/utils/charts/chartTypes';

import { Routes, STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { activePartnerAtom } from '../../../../state';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import { makeTextExcerpt } from '../../../../utils/testUtils';
import {
  getMonitorCellGraphRange,
  getMonitorChartGroups,
  parseMonitorData,
} from '../../helpers';

import SignalIcon from '../../../../assets/icons';
import { MonitorCellProps, MonitorCellStyles } from '../../types';
import {
  mapOutreachStatusToColor,
  mapOutreachStatusToLabel,
} from '../MonitorInfo/helpers';
import {
  CellActionsMenu,
  renderCustomTooltip,
} from './components/CellActionsMenu';
import CustomTooltip from './components/CustomTooltip';

import { useQuery } from '@apollo/client';
import { useVisibleInViewport } from 'plume-web-utils';
import {
  getMonitorDataLatestQuery,
  getMonitorHourlyFreq,
} from '../../services/monitorQueries';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

const MonitorCell: AdjustableComponent<MonitorCellProps, MonitorCellStyles> = ({
  name,
  supportOutreach,
  subscriberOutreach,
  supportWebhook,
  id,
  menuHandler,
  frequencyInHours = 0,
}) => {
  const { t } = useTranslation();
  const selectedPartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();
  const [graphData, setGraphData] = useState<ComposedChartDataItem[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const { ref, initialInView, isInView } = useVisibleInViewport<
    HTMLDivElement
  >();

  const { startDate, endDate } = useMemo(
    () => getMonitorCellGraphRange(frequencyInHours),
    [frequencyInHours],
  );

  const { data, loading } = useQuery(
    getMonitorHourlyFreq(id, startDate, endDate),
    { skip: !initialInView || !id },
  );

  const monitorData = useMemo(
    () => [...(data?.signal_3_p_evaluate_monitor_hourly_freq || [])].reverse(), // API doesn't have "asc" order implemented... so we have to reverse
    [data],
  );

  const { data: locationCountData } = useQuery(getMonitorDataLatestQuery(id), {
    skip: !initialInView || !id || frequencyInHours > 0,
  });

  useEffect(() => {
    if (loading || data === undefined) return;
    setGraphData(parseMonitorData(monitorData, frequencyInHours));
    setShowLoader(false);
  }, [loading, data]);

  const formatGraphTicks = (value: any, index: number): string => {
    if (
      index === 0 ||
      index === graphData.length - 1 ||
      index === Math.floor(graphData.length / 2)
    )
      return `${value}`;
    return '';
  };

  menuHandler = {
    ...menuHandler,
    viewMonitorDetails: () => {
      redirectToRoute(Routes.MonitorDetails, {
        partnerId: selectedPartner?.id,
        monitorId: id,
        eventId: 0,
      });
    },
  };

  const isNearRealTime = frequencyInHours === 0;
  const latestData = monitorData[monitorData.length - 1];
  const locationCount = (locationCountData
    ?.signal_3_p_evaluate_monitor_freq_aggregate.aggregate.count ||
    latestData?.location_count) as number;

  return (
    <div ref={ref} className="MonitorCell">
      <div className="MonitorCell__header">
        <Tooltip
          type="hoverType"
          position="below"
          alignment="left"
          label={name}
          overrideMaxCharacters
          openInPortal
          classes={(current) => ({
            ...current,
            bubble: `${current.bubble} MonitorCell__titleTooltip`,
          })}
        >
          <Button
            styleVariant="tertiary"
            onClick={() =>
              redirectToRoute(Routes.MonitorDetails, {
                partnerId: selectedPartner?.id,
                monitorId: id,
                eventId: 0,
              })
            }
            classes={(current) => ({
              ...current,
              root: `${current.root} MonitorCell__header`,
            })}
          >
            {makeTextExcerpt(name, 24)}
          </Button>
        </Tooltip>
      </div>
      <div className="MonitorCell__headerIcons">
        <Tooltip
          openInPortal
          label={
            isNearRealTime
              ? t('monitors.monitorCell.tooltips.nearRealTime')
              : t('monitors.monitorCell.tooltips.dataAsOf', {
                  date: dayjs
                    .utc(latestData?.ts)
                    .local()
                    .format(STANDARD_DATE_TIME_FORMAT),
                  interpolation: { escapeValue: false },
                })
          }
        >
          <SignalIcon
            className="MonitorCell__lastUpdateIcon"
            width={24}
            height={24}
            name={isNearRealTime ? 'clockLightning' : 'clock'}
          />
        </Tooltip>
        <CellActionsMenu id={id || name} menuHandler={menuHandler} />
      </div>
      <div className="MonitorCell__metrics-container">
        <div className="MonitorCell__affected-locations">
          <div className="MonitorCell__label">
            <div className="MonitorCell__tooltipLabel">
              {t('monitors.monitorCell.currentAffected')}
              <Tooltip
                openInPortal
                overrideMaxCharacters
                label={t('monitors.monitorCell.tooltips.affectedLocations')}
              >
                <Icons.CircleInfoIcon width={14} height={14} />
              </Tooltip>
            </div>
            <div className="MonitorCell__affected-locations__count">
              {(locationCount || 0).toLocaleString('en-US')}
            </div>
          </div>
        </div>
        {subscriberOutreach !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.subscriberOutreach')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status
                color={mapOutreachStatusToColor(subscriberOutreach.status)}
              />
              {mapOutreachStatusToLabel(subscriberOutreach.status)}
            </span>
          </div>
        )}
        {supportOutreach !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.supportOutreach')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status
                color={mapOutreachStatusToColor(supportOutreach.status)}
              />
              {mapOutreachStatusToLabel(supportOutreach.status)}
            </span>
          </div>
        )}
        {supportWebhook !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.supportWebhook')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status color={mapOutreachStatusToColor(supportWebhook.status)} />
              {mapOutreachStatusToLabel(supportWebhook.status)}
            </span>
          </div>
        )}
      </div>
      <div className="MonitorCell__chart-container">
        <PendingContent
          loading={showLoader}
          loader={() => <Spinner visible size="xsmall" />}
        >
          {isInView && (
            <ComposedChart
              curveType={'monotoneX'}
              data={graphData}
              groups={getMonitorChartGroups(1)}
              config={{
                showPercentageOnTooltip: false,
                xAxisLabelAngle: 0,
                showTooltipTitleForNumerical: true,
                showVerticalCartesianGrid: true,
                minHeight: 'auto',
                xAxisPadding: { left: 20, right: 20 },
                xAxisHeightOverride: 16,
                allowDecimalsY: false,
              }}
              stacked
              showDots={false}
              tickFormatter={formatGraphTicks}
              customTooltip={
                <CustomTooltip
                  data={graphData}
                  dataIdentifier="name"
                  renderTooltipContent={renderCustomTooltip}
                />
              }
            />
          )}
        </PendingContent>
      </div>
    </div>
  );
};

export default memo(MonitorCell);
